/**
 * Apply rack list edits from a Request Detail edit session on Save Changes (#995).
 */

import { api } from './api';
import {
  computeRepeatableOperations,
  type ComputedRepeatableOps,
} from './computeRepeatableOperations';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { mirrorRacksToOfflineRequestCache } from './rackOfflineSync';
import { toCreateRackPayload } from './rackPayload';
import { flushRepeatableInstanceAttachmentSectionRemaps } from './repeatableInstanceAttachmentRemapFlush';
import { deleteRepeatableUnitTolerating404 } from './repeatableUnitDelete';
import {
  LEGACY_RACK_SYNTHETIC_ID,
  validateSessionRackNames,
} from '../components/racks/rackListUtils';
import { RACK_REPEATABLE_ATTACHMENT_BASE_SECTIONS } from '../utils/repeatableInstanceAttachmentSections';

import type { RackConfiguration } from '../types/rack';

function isPersistedRackId(id: number | undefined): id is number {
  return id != null && id > 0;
}

function isSessionOnlyRackId(id: number | undefined): boolean {
  return id == null || id <= 0 || id === LEGACY_RACK_SYNTHETIC_ID;
}

/**
 * Canonicalize for content comparison: sort keys and drop undefined values so
 * two rows with identical CONTENT compare equal regardless of key insertion
 * order. Load-bearing since #997 Story 6.3: the flush baseline (raw API/mapper
 * rows, identity keys first) and the session rows (round-tripped through
 * `descriptorInstanceToRack`, identity keys appended last) are different object
 * lineages — a raw `JSON.stringify` flagged every rack as changed, PUTting the
 * whole list on save.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const entry = (value as Record<string, unknown>)[key];
      if (entry !== undefined) {
        sorted[key] = canonicalize(entry);
      }
    }
    return sorted;
  }
  return value;
}

function rackContentKey(rack: RackConfiguration): string {
  // `requestId` excluded too (#997 Story 6.3 review): it never changes for a
  // rack legitimately, but the engine conversion stamps `parseInt('o-…') → 0`
  // on offline-request session rows, which would keep every untouched rack
  // permanently "changed" against the cached baseline.
  const {
    id: _id,
    requestId: _rid,
    createdAt: _ca,
    updatedAt: _ua,
    ...rest
  } = rack;
  return JSON.stringify(canonicalize(rest));
}

function racksEqual(a: RackConfiguration[], b: RackConfiguration[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (rack, index) => rackContentKey(rack) === rackContentKey(b[index]),
  );
}

export function rackEditSessionHasChanges(
  baseline: RackConfiguration[],
  session: RackConfiguration[],
): boolean {
  return !racksEqual(baseline, session);
}

/**
 * Pure save-time diff for racks (#1501): consumed by this flush AND by the
 * aggregate save payload builder — one diff implementation, two consumers.
 */
export function computeRackOperations(
  baseline: RackConfiguration[],
  session: RackConfiguration[],
): ComputedRepeatableOps<RackConfiguration> {
  return computeRepeatableOperations(baseline, session, {
    isPersistedId: isPersistedRackId,
    isSessionOnlyId: isSessionOnlyRackId,
    contentKey: rackContentKey,
    stripForUpdate: rack => {
      const {
        id: _id,
        createdAt: _ca,
        updatedAt: _ua,
        requestId: _rid,
        ...data
      } = rack;
      return data;
    },
    // A legacy synthetic row whose content still equals its baseline
    // counterpart is display-only — not a create (pre-extraction rule).
    skipCreate: (rack, base) => {
      if (rack.id !== LEGACY_RACK_SYNTHETIC_ID) {
        return false;
      }
      const baselineLegacy = base.find(
        item => item.id === LEGACY_RACK_SYNTHETIC_ID,
      );
      return (
        !!baselineLegacy &&
        rackContentKey(baselineLegacy) === rackContentKey(rack)
      );
    },
  });
}

export async function flushRackEditSession(
  requestId: string,
  baseline: RackConfiguration[],
  session: RackConfiguration[],
  isOfflineRequest: boolean,
): Promise<RackConfiguration[]> {
  if (!rackEditSessionHasChanges(baseline, session)) {
    return session;
  }

  const rackNameError = validateSessionRackNames(session);
  if (rackNameError) {
    throw new Error(rackNameError);
  }

  const idKey = String(requestId);
  const useOnlineApi = networkService.isConnected() && !isOfflineRequest;

  const ops = computeRackOperations(baseline, session);

  for (const deletedId of ops.deletes) {
    if (useOnlineApi) {
      await deleteRepeatableUnitTolerating404(
        `/requests/${idKey}/racks/${deletedId}`,
      );
    } else {
      await offlineCache.addRackToOfflineQueue(
        idKey,
        'delete',
        undefined,
        deletedId,
      );
    }
  }

  const createdIdMap = new Map<number, number>();

  for (const { tempId, row } of ops.creates) {
    const payload = toCreateRackPayload({
      ...row,
      requestId: parseInt(idKey, 10) || row.requestId,
    });
    if (useOnlineApi) {
      const created = await api.post<RackConfiguration>(
        `/requests/${idKey}/racks`,
        payload,
      );
      if (tempId != null && created.id) {
        createdIdMap.set(tempId, created.id);
      }
    } else {
      await offlineCache.addRackToOfflineQueue(
        idKey,
        'create',
        payload,
        tempId,
      );
    }
  }

  for (const { id, data } of ops.updates) {
    if (useOnlineApi) {
      await api.put<RackConfiguration>(`/requests/${idKey}/racks/${id}`, data);
    } else {
      await offlineCache.addRackToOfflineQueue(idKey, 'update', data, id);
    }
  }

  const tempIdByClientKey = new Map(
    ops.creates.map(c => [c.clientKey, c.tempId]),
  );
  const orderedPersistedIds = ops.orderRefs
    .map(ref => {
      if (typeof ref === 'number') {
        return ref;
      }
      const tempId = tempIdByClientKey.get(ref);
      return tempId != null && createdIdMap.has(tempId)
        ? createdIdMap.get(tempId)!
        : null;
    })
    .filter((id): id is number => id != null);

  if (
    orderedPersistedIds.length > 0 &&
    JSON.stringify(orderedPersistedIds) !==
      JSON.stringify(ops.baselineOrderInSession)
  ) {
    if (useOnlineApi) {
      await api.patch<RackConfiguration[]>(`/requests/${idKey}/racks/reorder`, {
        rackIds: orderedPersistedIds,
      });
    } else {
      await offlineCache.addRackToOfflineQueue(
        idKey,
        'reorder',
        undefined,
        undefined,
        orderedPersistedIds,
      );
    }
  }

  await flushRepeatableInstanceAttachmentSectionRemaps(
    idKey,
    createdIdMap,
    RACK_REPEATABLE_ATTACHMENT_BASE_SECTIONS,
    useOnlineApi,
  );

  const normalizedSession = session.map(rack => {
    if (rack.id != null && createdIdMap.has(rack.id)) {
      return { ...rack, id: createdIdMap.get(rack.id)! };
    }
    return rack;
  });

  if (!useOnlineApi) {
    await offlineCache.updateCachedRequestRacks(idKey, normalizedSession);
  } else {
    await mirrorRacksToOfflineRequestCache(idKey, normalizedSession);
  }

  return normalizedSession;
}
